// State Machine Implementation - Simplified Application State Machine
// ✅ NO MAGIC STRINGS: Use enums/constants for all state and event names

import { GateManager } from "./GateManager";
import {
  AppState,
  StateEvent,
  StateTransition,
  StateMachineConfig,
  StateMachineContract,
  AppPhase,
  StateDataRequirements,
  StateAllowedRoutes,
} from "./types";

export interface StateTransitionWithGate extends StateTransition {
  gate?: string | null;
}

// Extend StateMachineConfig to support gates in transitions
export interface StateMachineConfigWithGates extends StateMachineConfig {
  transitions: StateTransitionWithGate[];
}

export class StateMachine implements StateMachineContract {
  private state: AppState;
  private readonly transitions: StateTransition[];
  private readonly phases: AppPhase[];
  private readonly onTransition?: (
    from: AppState,
    to: AppState,
    event: StateEvent,
  ) => void;
  private readonly initialState: AppState;
  private gateManager?: GateManager;
  private transitionGates: Map<string, string | null> = new Map(); // Map transition key to gate name

  constructor(config: StateMachineConfig, gateManager?: GateManager) {
    this.initialState = config.initialState;
    this.state = config.initialState;
    this.transitions = config.transitions;
    this.phases = config.phases;
    this.onTransition = config.onTransition;
    this.gateManager = gateManager;
  }

  /**
   * Set gate manager
   */
  setGateManager(gateManager: GateManager): void {
    this.gateManager = gateManager;
  }

  /**
   * Set gate for a transition
   */
  setTransitionGate(
    from: AppState,
    event: StateEvent,
    gate: string | null,
  ): void {
    const key = `${from}:${event}`;
    this.transitionGates.set(key, gate);
  }

  getState(): AppState {
    return this.state;
  }

  canTransition(event: StateEvent): boolean {
    const transition = this.transitions.find(
      (t) => t.from === this.state && t.event === event,
    );
    if (!transition) {
      return false;
    }

    // Check gate if gate manager is available
    const key = `${this.state}:${event}`;
    const gate = this.transitionGates.get(key);
    if (this.gateManager && gate) {
      if (this.gateManager.isGateClosed(gate)) {
        return false; // Gate is closed, transition blocked
      }
    }

    return true;
  }

  transition(event: StateEvent): void {
    const transition = this.transitions.find(
      (t) => t.from === this.state && t.event === event,
    );
    if (!transition) {
      throw new Error(`Invalid transition: ${this.state} + ${event}`);
    }

    // Check gate before transitioning
    const key = `${this.state}:${event}`;
    const gate = this.transitionGates.get(key);
    if (this.gateManager && gate) {
      if (this.gateManager.isGateClosed(gate)) {
        throw new Error(`Transition blocked: gate ${gate} is closed`);
      }
    }

    const prevState = this.state;
    this.state = transition.to;
    if (this.onTransition) {
      this.onTransition(prevState, this.state, event);
    }
  }

  getAvailableEvents(): StateEvent[] {
    return this.transitions
      .filter((t) => t.from === this.state)
      .map((t) => t.event);
  }

  getCurrentPhase(): AppPhase | null {
    return this.phases.find((phase) => phase.state === this.state) || null;
  }

  getDataRequirements(): StateDataRequirements | null {
    const currentPhase = this.getCurrentPhase();
    return currentPhase?.dataRequirements || null;
  }

  getAllowedRoutes(): StateAllowedRoutes | null {
    const currentPhase = this.getCurrentPhase();
    return currentPhase?.allowedRoutes || null;
  }

  reset(): void {
    this.state = this.initialState;
  }

  /**
   * Get all valid transitions from current state
   */
  getValidTransitions(): StateTransition[] {
    return this.transitions.filter((t) => t.from === this.state);
  }

  /**
   * Check if a specific transition is valid
   */
  isValidTransition(from: AppState, event: StateEvent, to: AppState): boolean {
    return this.transitions.some(
      (t) => t.from === from && t.event === event && t.to === to,
    );
  }

  /**
   * Get phase information for a specific state
   */
  getPhaseForState(state: AppState): AppPhase | null {
    return this.phases.find((phase) => phase.state === state) || null;
  }

  /**
   * Get all phases
   */
  getAllPhases(): AppPhase[] {
    return [...this.phases];
  }

  /**
   * Get gate name for a transition
   */
  getTransitionGate(from: AppState, event: StateEvent): string | null {
    const key = `${from}:${event}`;
    return this.transitionGates.get(key) || null;
  }

  /**
   * Check if exit gate from a specific state is closed
   * Returns true if any exit transition gate is closed
   */
  isExitGateClosed(state?: AppState): boolean {
    if (!this.gateManager) {
      return false; // No gate manager, assume open
    }

    const checkState = state || this.state;
    // Check all transitions from the specified state
    const exitTransitions = this.transitions.filter(
      (t) => t.from === checkState,
    );

    for (const transition of exitTransitions) {
      const key = `${checkState}:${transition.event}`;
      const gate = this.transitionGates.get(key);

      if (gate && this.gateManager.isGateClosed(gate)) {
        return true; // At least one exit gate is closed
      }
    }

    return false; // All exit gates are open
  }

  /**
   * Get all exit gates for a specific state (or current state if not specified)
   */
  getExitGates(
    state?: AppState,
  ): Array<{ event: StateEvent; gate: string | null; closed: boolean }> {
    const checkState = state || this.state;
    const exitTransitions = this.transitions.filter(
      (t) => t.from === checkState,
    );

    return exitTransitions.map((transition) => {
      const key = `${checkState}:${transition.event}`;
      const gate = this.transitionGates.get(key) || null;
      const closed =
        gate && this.gateManager ? this.gateManager.isGateClosed(gate) : false;

      return {
        event: transition.event,
        gate,
        closed,
      };
    });
  }
}
